import React, { useCallback } from 'react';

import { OutlineButton } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import { centsToDollarString } from 'utils';

import { ScanRewards } from 'components/app-header/loyalty/scan-rewards';
import { DialogFooter } from 'components/sticky-footer';
import { Tab, TabPanel, Tabs } from 'components/tabs';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import { useAndroidBackButton } from 'hooks/use-android-back-button';
import useEffectOnce from 'hooks/use-effect-once';
import useMaximumScreenBrightness from 'hooks/use-maximum-screen-brightness';
import { usePaymentContext } from 'state/payment';
import { DrawerTab, useUIContext } from 'state/ui';
import { EventName, emitEvent } from 'utils/event-hub';

import { AppBarStyle, Section, StyledTabPanels, StyledTabs } from './loyalty.styled';
import { LoyaltyTimCard } from './tim-card';

interface ILoyalty {
  onDismiss: (e?: React.SyntheticEvent) => void;
}

interface IAppBar {
  children: React.ReactNode;
}

export const AppBar = ({ children }: IAppBar) => {
  return <AppBarStyle>{children}</AppBarStyle>;
};

function a11yProps(index: any) {
  return {
    id: `drawer-tab-${index}`,
    ariaControls: `drawer-tabpanel-${index}`,
    boldText: true,
  };
}

const Loyalty: React.FC<ILoyalty> = ({ onDismiss }) => {
  const { formatMessage } = useIntl();
  const { activeDrawerTab, setActiveDrawerTab } = useUIContext();
  const handleChange = useCallback(
    (event: React.ChangeEvent<{}>, newValue: DrawerTab) => {
      event.preventDefault();
      setActiveDrawerTab(newValue);
    },
    [setActiveDrawerTab]
  );

  useEffectOnce(() => {
    emitEvent(EventName.SCREEN_RENDER, {
      screenId: 'scan',
    });
  });

  const inactiveStyle = {
    opacity: 0.5,
  };

  useMaximumScreenBrightness();
  useAndroidBackButton(onDismiss);

  enum TabIndex {
    TIMS_REWARDS = 0,
    TIMS_CARD = 1,
  }

  const { loyaltyDetails } = useLoyaltyActiveCard();
  const rewardsPoints = loyaltyDetails ? loyaltyDetails.points : 0;

  let dollars = '0';
  let cents = '00';
  let centsBalance = 0;

  const { getPrepaidPaymentMethod, getBalanceFromPaymentMethods } = usePaymentContext();

  const prepaidPaymentMethod = getPrepaidPaymentMethod();
  if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
    centsBalance = getBalanceFromPaymentMethods(prepaidPaymentMethod!);
    const balance = centsToDollarString(centsBalance);
    [dollars, cents] = balance.split('.');
  }
  const { currencySymbol, currencyIsSuffix } = useUIContext();

  const timCardBalance = currencyIsSuffix
    ? `${dollars}.${cents}${currencySymbol}`
    : `${currencySymbol}${dollars}.${cents}`;

  return (
    <>
      <Section>
        <StyledTabs data-testid="styled-tabs">
          <AppBar>
            <Tabs
              color="inverted"
              inactiveStyle={inactiveStyle}
              selectedIndex={activeDrawerTab}
              onChange={handleChange}
            >
              <Tab
                label={formatMessage({ id: 'timsRewardsHeader' })}
                {...a11yProps(TabIndex.TIMS_REWARDS)}
                value={TabIndex.TIMS_REWARDS}
                secondaryInfo={`${rewardsPoints} Points`}
              />
              <Tab
                label={formatMessage({ id: 'timsCardHeader' })}
                {...a11yProps(TabIndex.TIMS_CARD)}
                value={TabIndex.TIMS_CARD}
                secondaryInfo={timCardBalance}
              />
            </Tabs>
          </AppBar>
        </StyledTabs>
        <StyledTabPanels data-testid="styled-tab-panels">
          <TabPanel
            value={activeDrawerTab}
            index={TabIndex.TIMS_REWARDS}
            id={`drawer-tabpanel-${TabIndex.TIMS_REWARDS}`}
            ariaLabeledBy={`drawer-tab-${TabIndex.TIMS_REWARDS}`}
          >
            <ScanRewards onDismiss={onDismiss} />
          </TabPanel>
          <TabPanel
            value={activeDrawerTab}
            index={TabIndex.TIMS_CARD}
            id={`drawer-tabpanel-${TabIndex.TIMS_CARD}`}
            ariaLabeledBy={`drawer-tab-${TabIndex.TIMS_CARD}`}
          >
            <LoyaltyTimCard onDismiss={onDismiss} />
          </TabPanel>
        </StyledTabPanels>
      </Section>
      <DialogFooter
        boxProps={{
          position: 'fixed',
          bottom: '0px',
          width: '100%',
          padding: '16px',
        }}
        elements={[
          {
            element: (
              <OutlineButton data-testid="tab-close-button" onClick={onDismiss} fullWidth>
                {formatMessage({ id: 'close' })}
              </OutlineButton>
            ),
          },
        ]}
      />
    </>
  );
};

export default Loyalty;
